import dayjs from 'dayjs'
import { ValidLocale } from '@localisation'

export type DayJSInput = Date | dayjs.Dayjs | string | undefined

const _toDayJs = (date: DayJSInput): dayjs.Dayjs => {
  if (!date) return dayjs()
  if (typeof date === 'string') return dayjs(date)
  if (date instanceof Date) return dayjs(date)
  return date
}

const getProbableLocale = (): string => {
  const locale = dayjs().locale()

  switch (locale) {
    // Most of the English speakers are located in
    // Finland (or other EU country) so there is no
    // point formatting English locales US way.
    // Default to Finnish date format if user is
    // speaking English as it is easy to understand
    // for Europeans
    case ValidLocale.EN:
      return ValidLocale.FI
    // Same case applies to Swedish speakers.
    case ValidLocale.SV:
      return ValidLocale.FI
    default:
      return locale
  }
}

export const formatDate = (date: DayJSInput): string =>
  _toDayJs(date).locale(getProbableLocale()).format('L')

export const formatDateTime = (date: DayJSInput): string => {
  const at = _toDayJs(date).locale(getProbableLocale())
  return `${at.format('L')} ${at.format('HH:mm')}`
}

export const formatShortDate = (date: DayJSInput): string =>
  _toDayJs(date).locale(getProbableLocale()).format('DD.MM.')

export const formatRelativeTime = (date: DayJSInput): string => _toDayJs(date).fromNow()

export type RecentTime =
  | { kind: 'justNow' }
  | { kind: 'today' }
  | { kind: 'relative'; text: string }
  | { kind: 'date'; text: string }

const SAME_DAY_HOUR_LIMIT = 12
const RELATIVE_DAY_LIMIT = 7

export const formatRecentTime = (
  date: DayJSInput,
  options?: { dateOnly?: boolean }
): RecentTime => {
  const at = _toDayJs(date)
  const now = dayjs()
  if (options?.dateOnly) {
    if (at.isSame(now, 'day')) return { kind: 'today' }
    return { kind: 'date', text: formatDate(at) }
  }
  const seconds = now.diff(at, 'second')
  if (seconds < 0) return { kind: 'date', text: formatDate(at) }
  if (seconds < 60) return { kind: 'justNow' }
  if (at.isSame(now, 'day')) {
    const hours = now.diff(at, 'hour')
    if (hours < SAME_DAY_HOUR_LIMIT) return { kind: 'relative', text: at.fromNow() }
    return { kind: 'today' }
  }
  if (now.startOf('day').diff(at.startOf('day'), 'day') < RELATIVE_DAY_LIMIT) {
    return { kind: 'relative', text: at.fromNow() }
  }
  return { kind: 'date', text: formatDate(at) }
}
